// ludoBoard.js
// Single source of truth for the 15x15 Ludo grid mapping.
// All coordinates are [row, col], 0-indexed, matching the reference board:
//   Blue = top-left, Red = top-right, Green = bottom-right, Yellow = bottom-left.
// The main 52-cell loop below was derived and validated programmatically
// (graph walk over the actual non-home, non-center grid cells) so every
// cell connects correctly and each color's start is exactly 13 cells
// apart (52 / 4 colors = 13), preserving Ludo's rotational symmetry.

export const GRID_SIZE = 15;

export const COLORS = ['blue', 'red', 'green', 'yellow'];

export const COLOR_HEX = {
  blue: '#2BAEFC',
  red: '#EC1B20',
  green: '#089D4B',
  yellow: '#FBC02D',
};

export const COLOR_HEX_DARK = {
  blue: '#1789CE',
  red: '#B3161A',
  green: '#067A3A',
  yellow: '#C99A0B',
};

export const PLAYER_LABEL = {
  blue: 'Blue',
  red: 'Red',
  green: 'Green',
  yellow: 'Yellow',
};

// ---------------------------------------------------------------------
// THE MAIN 52-CELL OUTER TRACK (validated loop, clockwise from Blue exit)
// ---------------------------------------------------------------------
export const MAIN_TRACK = [
  [6, 1], [6, 2], [6, 3], [6, 4], [6, 5],
  [5, 6], [4, 6], [3, 6], [2, 6], [1, 6], [0, 6],
  [0, 7],
  [0, 8], [1, 8], [2, 8], [3, 8], [4, 8], [5, 8],
  [6, 9], [6, 10], [6, 11], [6, 12], [6, 13],
  [6, 14],
  [7, 14],
  [8, 14],
  [8, 13], [8, 12], [8, 11], [8, 10], [8, 9],
  [9, 8], [10, 8], [11, 8], [12, 8], [13, 8],
  [14, 8],
  [14, 7],
  [14, 6],
  [13, 6], [12, 6], [11, 6], [10, 6], [9, 6],
  [8, 5], [8, 4], [8, 3], [8, 2], [8, 1],
  [8, 0],
  [7, 0],
  [6, 0],
];
// length = 52

// Each color's pawn enters the main track at one of these indices, then
// proceeds 51 steps around MAIN_TRACK before peeling into its own
// 5-cell colored home stretch, ending in a 1-cell finish tile.
export const START_INDEX = {
  blue: 0, // [6,1]
  red: 13, // [1,8]
  green: 26, // [8,13]
  yellow: 39, // [13,6]
};

// Colored home-stretch (5 cells) + final finish tile (1 cell) per color.
export const HOME_STRETCH = {
  blue: [[7, 1], [7, 2], [7, 3], [7, 4], [7, 5]],
  red: [[1, 7], [2, 7], [3, 7], [4, 7], [5, 7]],
  green: [[7, 13], [7, 12], [7, 11], [7, 10], [7, 9]],
  yellow: [[13, 7], [12, 7], [11, 7], [10, 7], [9, 7]],
};

export const FINISH_TILE = {
  blue: [7, 6],
  red: [6, 7],
  green: [7, 8],
  yellow: [8, 7],
};

// Star (safe) cells visible on the reference board, one per arm.
export const STAR_CELLS = [
  [2, 6],
  [8, 2],
  [6, 12],
  [12, 8],
];

// Each color's own entry square on the main track is also always safe
// (classic rule: no captures on starting squares).
export const ENTRY_SAFE_CELLS = COLORS.map((c) => MAIN_TRACK[START_INDEX[c]]);

export function isSafeCell(row, col) {
  return (
    STAR_CELLS.some(([r, c]) => r === row && c === col) ||
    ENTRY_SAFE_CELLS.some(([r, c]) => r === row && c === col)
  );
}

// ---------------------------------------------------------------------
// HOME YARD (BASE) TOKEN SLOT POSITIONS - the 4 little circles per corner
// Fractional row/col positions (in grid-cell units) used to place the
// 4 resting tokens inside each colored base square's white inner panel.
// ---------------------------------------------------------------------
export const BASE_SLOTS = {
  blue: [
    [1.82, 1.82], [1.85, 3.25], [3.25, 1.85], [3.25, 3.25],
  ],
  red: [
    [1.80, 10.82], [1.85, 12.19], [3.25, 10.80], [3.25, 12.25],
  ],
  green: [
    [10.80, 10.82], [10.85, 12.19], [12.25, 10.80], [12.25, 12.25],
  ],
  yellow: [
    [10.80, 1.82], [10.85, 3.19], [12.25, 1.80], [12.25, 3.25],
  ],
};

// ---------------------------------------------------------------------
// Step semantics for a single token, per color:
//   step 0        -> still in base (not on board)
//   step 1..51    -> on MAIN_TRACK, at index (START_INDEX[color] + step - 1) % 52
//   step 52..56   -> on HOME_STRETCH[color][step-52]   (5 cells)
//   step 57       -> on FINISH_TILE[color]              (final resting cell)
//   step 57 reached = token has finished (reached home)
// ---------------------------------------------------------------------
export const STEPS_TO_FINISH = 57;

export function getCellForStep(color, step) {
  if (step <= 0) return null;
  if (step <= 51) {
    const idx = (START_INDEX[color] + (step - 1)) % 52;
    return MAIN_TRACK[idx];
  }
  if (step <= 56) {
    return HOME_STRETCH[color][step - 52];
  }
  if (step === 57) {
    return FINISH_TILE[color];
  }
  return null;
}

// Returns true if landing on `step` for `color` lands on a main-track
// cell that is shared/visible to other colors (i.e. capture is possible).
export function isOnSharedTrack(step) {
  return step >= 1 && step <= 51;
}

export function countColorOnSharedCell(tokens, color, row, col) {
  let count = 0;
  for (const t of tokens[color] || []) {
    if (t.finished || t.step === 0 || !isOnSharedTrack(t.step)) continue;
    const cell = getCellForStep(color, t.step);
    if (cell && cell[0] === row && cell[1] === col) count += 1;
  }
  return count;
}

export function hasBlockOnCell(tokens, playerColors, row, col) {
  if (isSafeCell(row, col)) return false;
  return playerColors.some((c) => countColorOnSharedCell(tokens, c, row, col) >= 2);
}

// Opponents may always land on a shared cell; blocks only prevent capture, not entry.
export function canLandAtStep(tokens, playerColors, movingColor, targetStep) {
  if (targetStep === STEPS_TO_FINISH || !isOnSharedTrack(targetStep)) return true;
  return getCellForStep(movingColor, targetStep) != null;
}

export function tryCaptureOnLanding(tokens, playerColors, movingColor, row, col) {
  if (isSafeCell(row, col)) return false;
  if (hasBlockOnCell(tokens, playerColors, row, col)) return false;

  let captured = false;
  for (const c of playerColors) {
    if (c === movingColor) continue;
    if (countColorOnSharedCell(tokens, c, row, col) !== 1) continue;
    for (const t of tokens[c]) {
      if (t.finished || t.step === 0 || !isOnSharedTrack(t.step)) continue;
      const cell = getCellForStep(c, t.step);
      if (cell && cell[0] === row && cell[1] === col) {
        t.step = 0;
        captured = true;
      }
    }
  }
  return captured;
}

export function applyBreakStackPenalty(tokens, playerColors, movingColor, fromRow, fromCol) {
  if (fromRow == null || fromCol == null || isSafeCell(fromRow, fromCol)) return false;

  const myLeft = countColorOnSharedCell(tokens, movingColor, fromRow, fromCol);
  if (myLeft !== 1) return false;

  let oppOnCell = 0;
  for (const c of playerColors) {
    if (c === movingColor) continue;
    oppOnCell += countColorOnSharedCell(tokens, c, fromRow, fromCol);
  }
  if (oppOnCell < 1) return false;

  for (const t of tokens[movingColor]) {
    if (t.finished || t.step === 0 || !isOnSharedTrack(t.step)) continue;
    const cell = getCellForStep(movingColor, t.step);
    if (cell && cell[0] === fromRow && cell[1] === fromCol) {
      t.step = 0;
      return true;
    }
  }
  return false;
}
